import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, ReplaySubject, Subject, of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { Signature } from '../../models/signature';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { ConfigService } from '../services/config.service';
import { SignatureService } from '../services/signature.service';
import { MomentModule } from 'ngx-moment';
import { UserIdToNamePipe } from '../pipes/user-id-to-name.pipe';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

interface SignableParams {
  type: string;
  id: number;
  deliveryId?: number;
}

@Component({
    selector: 'app-signature-list',
    templateUrl: './signature-list.component.html',
    styleUrls: ['./signature-list.component.css'],
    providers: [SignatureService],
    standalone: true,
    imports: [NgIf, NgFor, AsyncPipe, UserIdToNamePipe, MomentModule]
})
export class SignatureListComponent implements AfterViewInit {
  @Input() set signable(value: SignableParams) {
    this.signableSubject.next(value);
  }

  @Output() signaturesLoaded = new EventEmitter<Signature[]>();

  refreshSignatures = new Subject<void>();
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;

  signableSubject = new ReplaySubject<SignableParams | null>(1);

  signatures$ = this.signableSubject.asObservable().pipe(
    mergeMap((signable) =>
      this.refreshSignatures.asObservable().pipe(
        map(() => signable),
        startWith(signable)
      )
    ),
    switchMap((signable) => this.getSignatures(signable)),
    startWith([])
  );
  constructor(
    private signatureService: SignatureService,
    private dialog: MatDialog
  ) {}

  private getSignatures(signable: SignableParams | null) {
    if (!signable) {
      return of([]);
    }
    return this.signatureService
      .getForSignable(signable.type, signable.id)
      .pipe(
        map((signatures) =>
          signatures.filter(
            ({ delivery_id }) =>
              !signable.deliveryId || delivery_id === signable.deliveryId
          )
        ),
        catchError(() => of([])),
        tap((signatures) => this.signaturesLoaded.emit(signatures))
      );
  }

  ngAfterViewInit(): void {
    this.refreshSignatures.next();
  }

  deleteSignature(signature: Signature) {
    this.dialog
      .open<ConfirmDialogComponent, any, any>(ConfirmDialogComponent, {
        data: {
          type: 'delete',
          title: 'Delete signature',
          message: 'Are you sure you want to delete this signature?'
        }
      })
      .afterClosed()
      .pipe(
        mergeMap((result: boolean) => {
          if (result) {
            return this.doDelete(signature);
          }
          return EMPTY;
        })
      )
      .subscribe();
  }

  private doDelete(signature: Signature) {
    return this.signatureService
      .delete(signature.id)
      .pipe(tap(() => this.refreshSignatures.next()));
  }
}
